<script setup lang="ts">
import { readinessModule } from '@/store/readiness/module'
import { screenModule } from '@/store/screen/module'
import { userModule } from '@/store/user/module'
import { computed } from 'vue'

const insightRows = computed(() => {
    if (!passerAverages.value) {
        return []
    }
    return [
        {
            factor: 'Overall Percent Correct*',
            passerValue: passerAverages.value.overallPercentCorrect,
            userValue: overallPercentCorrect.value,
            maxValue: 100,
            percentage: true,
        },
        {
            factor: 'Question Bank Completion',
            passerValue: passerAverages.value.questionBankCompletionPercent,
            userValue: percentBankCompleted.value,
            maxValue: 100,
            percentage: true,
        },
        {
            factor: 'Latest Mock Exam Score',
            passerValue: passerAverages.value.latestMockExamScore,
            userValue: lastMockExamScore.value,
            maxValue: 100,
            percentage: true,
        },
        {
            factor: 'Average Study Days Per Week**',
            passerValue: passerAverages.value.avgStudyDaysPerWeek,
            userValue: averageStudyDaysPerWeek.value,
            maxValue: 7,
        },
        {
            factor: 'Average Questions Per Week**',
            passerValue: passerAverages.value.avgQuestionsPerWeek,
            userValue: averageAnswersPerWeek.value,
            maxValue: passerAverages.value.avgQuestionsPerWeek * 2,
        },
    ]
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const isMobile = computed(() => {
    return screenModule.getters.getBreakpoint() === 'black-bear'
})

const overallPercentCorrect = computed(() => {
    return readinessModule.getters.getOverallPercentCorrect()
})

const percentBankCompleted = computed(() => {
    return readinessModule.getters.getPercentQuestionBankComplete()
})

const lastMockExamScore = computed(() => {
    return readinessModule.getters.getLatestMockExamScore()
})

const averageStudyDaysPerWeek = computed(() => {
    return readinessModule.getters.getAverageStudyDaysPerWeek()
})

const averageAnswersPerWeek = computed(() => {
    return readinessModule.getters.getAverageQuestionsPerWeek()
})

const passerAverages = computed(() => {
    return readinessModule.getters.getPasserAverages()
})

const calculateFillWidthPercent = (params: {
    userVal: number
    maxVal: number
}) => {
    if (params.userVal < 0) {
        return '0%'
    }
    if (params.userVal > params.maxVal) {
        return '100%'
    }
    const fillPercentage = (params.userVal / params.maxVal) * 100
    return `${fillPercentage}%`
}

const calculatePasserIndicatorOffsetPixels = (params: {
    passerVal: number
    maxVal: number
}) => {
    const leftOffset = (params.passerVal / params.maxVal) * 100
    return `calc(${leftOffset}% - ${isDarkMode.value ? 3 : 2}px)` // offset width of element and its border in dark mode
}
</script>

<template>
    <div class="passer-insights">
        <template v-if="isMobile">
            <div class="passer-insights__mobile-factors-head" v-dark>
                Key Passer Factors
            </div>
            <div class="passer-insights__mobile-factors-container">
                <div
                    v-for="(insight, index) in insightRows"
                    :key="index"
                    class="passer-insights__mobile-factor-container"
                    v-dark
                >
                    <div class="passer-insights__mobile-factor-title">
                        {{ insight.factor }}
                    </div>
                    <div class="passer-insights__mobile-factor-values" v-dark>
                        <div class="passer-insights__mobile-factor">
                            You: <span class="passer-insights__mobile-factor-value">
                                {{
                                    insight.userValue === -1 ? '--' : insight.userValue
                                }}<span v-if="insight.percentage">%</span>
                            </span>
                        </div>
                        <div class="passer-insights__mobile-factor">
                            <div class="passer-insights__mobile-passer-key" v-dark />
                            Passers: <span class="passer-insights__mobile-factor-value">
                                {{ insight.passerValue }}<span v-if="insight.percentage">%</span>
                            </span>
                        </div>
                    </div>
                    <div class="passer-insights__progress">
                        <div
                            class="passer-insights__progress-container
                        passer-insights__progress-container--mobile"
                            v-dark
                        >
                            <div
                                class="passer-insights__progress-fill"
                                :class="{
                                    'passer-insights__progress-fill--zero': !insight.userValue || insight.userValue < 0,
                                }"
                                :style="{
                                    'width': calculateFillWidthPercent({
                                        userVal: insight.userValue,
                                        maxVal: insight.maxValue,
                                    }),
                                }"
                                v-dark
                            />
                            <div
                                class="passer-insights__passer-progress-indicator
                                passer-insights__passer-progress-indicator--dynamic
                                passer-insights__passer-progress-indicator--mobile"
                                :style="{
                                    left: calculatePasserIndicatorOffsetPixels({
                                        passerVal: insight.passerValue,
                                        maxVal: insight.maxValue,
                                    })
                                }"
                                v-dark
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="passer-insights__foot-notes">
                <div class="passer-insights__note" v-dark>
                    *Overall percent correct of all question attempts <br />
                    **Includes only your past 30 days of data to prevent outliers
                </div>
            </div>
        </template>
        <template v-else>
            <div class="passer-insights__column-labels" v-dark>
                <div class="passer-insights__column-label passer-insights__column-label--col-1">Key Passer Factors</div>
                <div class="passer-insights__column-label passer-insights__column-label--col-2">Passer Average</div>
                <div class="passer-insights__column-label passer-insights__column-label--col-1">Your Profile</div>
            </div>
            <div
                v-for="(insight, index) in insightRows"
                :key="index"
                class="passer-insights__row"
                v-dark
            >
                <div class="passer-insights__factor">{{ insight.factor }}</div>
                <div class="passer-insights__passer-val" v-dark>
                    {{
                        insight.passerValue
                    }}<span v-if="insight.percentage" class="passer-insights__percent">%</span>
                </div>
                <div class="passer-insights__progress">
                    <span class="passer-insights__user-val">
                        {{
                            insight.userValue === -1 ? '--' : insight.userValue
                        }}<span v-if="insight.percentage" class="passer-insights__percent">%</span>
                    </span>
                    <div class="passer-insights__progress-container" v-dark>
                        <div
                            class="passer-insights__progress-fill"
                            :class="{
                                'passer-insights__progress-fill--zero': !insight.userValue || insight.userValue < 0,
                            }"
                            :style="{
                                'width': calculateFillWidthPercent({
                                    userVal: insight.userValue,
                                    maxVal: insight.maxValue,
                                }),
                            }"
                            v-dark
                        />
                        <div
                            class="passer-insights__passer-progress-indicator
                            passer-insights__passer-progress-indicator--dynamic"
                            :style="{
                                left: calculatePasserIndicatorOffsetPixels({
                                    passerVal: insight.passerValue,
                                    maxVal: insight.maxValue,
                                })
                            }"
                            v-dark
                        />
                    </div>
                </div>
            </div>
            <div class="passer-insights__foot-notes">
                <div class="passer-insights__note" v-dark>
                    *Overall percent correct of all question attempts <br />
                    **Includes only your past 30 days of data to prevent outliers
                </div>
                <div class="passer-insights__key">
                    <div class="passer-insights__passer-progress-indicator" v-dark />
                    <span class="passer-insights__passer-progress-key">Passer Average</span>
                </div>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.passer-insights {
    width: 643px;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-left: 3px;

    @include breakpoint(black-bear) {
        width: 100%;
        gap: 8px;
        padding-left: 0;
    }

    &__column-labels {
        width: 100%;
        display: flex;
        padding-left: 6px;
        box-sizing: border-box;
        border-bottom: 1px solid $fog;
        color: $slate;

        &--dark {
            color: $fog;
            border-color: $slate;
        }
    }

    &__row {
        display: flex;
        width: 100%;
        justify-content: center;
        padding: 12px 0 11px 6px;
        box-sizing: border-box;
        border-bottom: 1px solid $fog;

        &--dark {
            border-color: $slate;
        }
    }

    &__factor {
        width: 268px;
        line-height: 20px;
        font-size: 15px;
    }

    &__passer-val {
        width: 33px;
        margin: 0 20px;
        text-align: right;
        line-height: 20px;
        color: $ash;

        &--dark {
            color: $white;
        }
    }

    &__progress {
        width: 293px;
        display: flex;
        align-items: center;

        @include breakpoint(black-bear) {
            width: 100%;
        }
    }

    &__user-val {
        display: block;
        width: 51px;
        text-align: right;
        line-height: 20px;
        font-weight: 600;
        margin-right: 12px;
    }

    &__percent {
        font-weight: 400;
    }

    &__progress-container {
        height: 12px;
        width: 230px;
        background-color: $pearl;
        border: 1px solid rgb($pewter, 0.85);
        border-radius: 2px;
        position: relative;

        &--mobile {
            height: 6px;
            width: 100%;
            border-color: rgb($pewter, 0.3);

            &--dark {
                border: none;
            }
        }

        &--dark {
            background-color: $jet;
            border-color: $slate;
        }
    }

    &__progress-fill {
        height: 100%;
        border-radius: 2px;
        position: relative;
        top: -1px;
        left: -1px;
        border: 1px solid $midnight-blue;
        background-color: $brand-blue;

        &--dark {
            background-color: $baby-blue;
            border-color: $baby-blue;
        }
        &--zero {
            border: none;
        }
    }

    &__passer-progress-indicator {
        height: 20px;
        width: 4px;
        background-color: $brand-black;
        border-radius: 2px;
        z-index: 1;
        box-sizing: border-box;

        &--mobile {
            height: 16px;
        }

        &--dark {
            background-color: $beach-sand;
            border: 1px solid $jet;
            width: 6px;
        }

        &--dynamic {
            position: absolute;
            top: -4px;

            @include breakpoint(black-bear) {
                top: -5px;
            }
        }
    }

    &__column-label {
        font-size: 13px;
        line-height: 18px;
        padding-bottom: 5px;

        &--col-1 {
            width: 238px;
        }

        &--col-2 {
            margin-right: 32px;
        }
    }

    &__foot-notes {
        width: 100%;
        display: flex;
        justify-content: space-between;
        margin-top: 8px;

        @include breakpoint(black-bear) {
            margin-top: 0;
        }
    }

    &__note {
        width: 375px;
        color: $slate;
        font-size: 12px;
        line-height: 16px;

        @include breakpoint(black-bear) {
            padding-left: 15px;
        }

        &--dark {
            color: $fog;
        }
    }

    &__key {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__mobile-factors-head {
        color: $slate;
        font-size: 14px;
        font-weight: 500;
        line-height: 19px;
        padding-left: 15px;
        box-sizing: border-box;
        width: 100%;

        &--dark {
            color: $fog;
        }
    }

    &__mobile-factors-container {
        min-width: 339px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__mobile-factor-container {
        display: flex;
        flex-direction: column;
        background-color: $white;
        border: 1px solid $fog;
        border-radius: 8px;
        padding: 14px 15px 12px;
        gap: 8px;

        &--dark {
            background-color: $brand-black;
            border: none;
        }
    }

    &__mobile-factor-title {
        font-size: 17px;
        line-height: 25px;
        letter-spacing: -0.1px;
        font-weight: 600;
    }

    &__mobile-factor-values {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        color: $ash;
        text-align: right;
        font-size: 15px;
        line-height: 20px;

        &--dark {
            color: $gray-background;
        }
    }

    &__mobile-factor {
        position: relative;
    }

    &__mobile-factor-value {
        font-weight: 600;
    }

    &__mobile-passer-key {
        position: absolute;
        left: -10px;
        top: 5px;
        height: 10px;
        width: 4px;
        background-color: rgb($brand-black, 0.5);
        border-radius: 4px;

        &--dark {
            background-color: $beach-sand;
        }
    }

    &__passer-progress-key {
        font-size: 14px;
    }
}
</style>
